import useToast from "@repo/core/components/core/Toast";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { ROUTES } from "@repo/core/routes";
import { apiInstance } from "@repo/core/services/instances/api";
import { wayupApiInstance } from "@repo/core/services/instances/wayup-api";
import { ProposalsResource } from "@repo/core/services/resources/proposals";
import { ClientResource } from "@repo/core/services/resources/client";
import { RequisitionsResource } from "@repo/core/services/resources/requisitions";
import useAuthStore from "@repo/core/stores/auth";
import { useDocumentCaptureStore } from "@repo/core/stores/documentCapture";
import { getCleanCPF } from "@repo/core/utils/string.utils";
import { usePreProposalStore } from "../../store/preProposalStore";
import type { QueueProposalEditMode } from "../../store/preProposalStore";
import { WEB_BIOMETRY_STORAGE } from "../../screens/BiometryValidation/utils";
import {
  applyProposalReassessmentFromSituation,
  mapProposalToProposalData,
  mapWayupProposalPhoneTypeToProposalData,
  mergeProposalDataWithClientInfoFallback
} from "../../screens/InfoSecurity/mappers";

import { NO_AVAILABLE_PRODUCTS_TOAST } from "../../screens/SelectProduct/utils";
import { resolveProductForPreProposal } from "./resolveProduct";
import { getSingleValue, hasMultipleValues, hasNoValues } from "./utils";

const proposalsResource = new ProposalsResource(apiInstance);
const clientResource = new ClientResource(apiInstance);
const requisitionsResource = new RequisitionsResource(wayupApiInstance);
const NEW_PROPOSAL_QUERY_KEYS = new Set([
  "requiredDocuments",
  "proposalDocumentsList",
  "existUnicoRecord",
  "bioTokenIdReference",
  "wayupUnicoStagesPerformed",
  "lastCapture"
]);

export const usePreProposal = () => {
  const preProposalStore = usePreProposalStore();
  const resetDocumentCapture = useDocumentCaptureStore(
    (state) => state.resetDocumentCapture
  );
  const { user } = useAuthStore();
  const { push, replace } = useNavigation();
  const queryClient = useQueryClient();
  const toast = useToast();

  const getPreProposalInfo = async (proposalId: string) => {
    console.log("proposalId", proposalId);
    // TODO: Implementar a lógica para buscar a proposta no backend
  };

  const syncPreProposal = async () => {
    // TODO: Implementar a lógica para enviar a proposta para o backend
  };

  const clearPreProposal = () => {
    preProposalStore.resetPreProposalStore();
  };

  const resetNewProposalState = async () => {
    const proposalQueryFilter = {
      predicate: (query: { queryKey: readonly unknown[] }) =>
        NEW_PROPOSAL_QUERY_KEYS.has(String(query.queryKey[0]))
    };

    await queryClient.cancelQueries(proposalQueryFilter);
    queryClient.removeQueries(proposalQueryFilter);
    clearPreProposal();
    resetDocumentCapture();
    WEB_BIOMETRY_STORAGE.clearProposalArtifacts();
  };

  const startPreProposal = async () => {
    if (!user?.tokenData) {
      toast.error("Sua sessão expirou", "Por favor, faça login novamente");
      return;
    }

    await resetNewProposalState();

    const productResolution = await resolveProductForPreProposal(
      user.tokenData.lstProdutos,
      proposalsResource
    );

    if (productResolution.status === "unavailable") {
      toast.error(
        NO_AVAILABLE_PRODUCTS_TOAST.title,
        NO_AVAILABLE_PRODUCTS_TOAST.description
      );
      replace(ROUTES.home);
      return;
    }

    if (productResolution.status === "needsSelection") {
      push(ROUTES.preProposalsSelectProduct);
      return;
    }

    preProposalStore.setProposalData({
      strProduto: productResolution.productId
    });

    const lstPontosAtendimento = user.tokenData.lstPontosAtendimento;

    if (
      hasNoValues(lstPontosAtendimento) ||
      hasMultipleValues(lstPontosAtendimento)
    ) {
      push(ROUTES.preProposalsSelectServicePoint);
      return;
    }

    const singleServicePoint = getSingleValue(lstPontosAtendimento);
    preProposalStore.setProposalData({
      strPontoAtendimento: singleServicePoint
    });

    push(ROUTES.preProposalsSelectAttendant);
  };

  const startQueueProposalEdit = async (
    proposalId: string,
    queueEditMode: QueueProposalEditMode
  ): Promise<void> => {
    try {
      clearPreProposal();
      const [
        proposalDetailsResponse,
        proposalSituationResponse,
        wayupProposalDataResponse
      ] = await Promise.all([
        proposalsResource.getProposalDetails(proposalId),
        proposalsResource.getProposalSituation(proposalId),
        requisitionsResource.getProposalData(proposalId).catch((err) => {
          console.error(
            "Error loading Wayup API proposal data for proposal edit:",
            err
          );
          return undefined;
        })
      ]);
      const proposalData = applyProposalReassessmentFromSituation(
        {
          ...mapProposalToProposalData(proposalDetailsResponse.data),
          ...mapWayupProposalPhoneTypeToProposalData(
            wayupProposalDataResponse?.data || {}
          )
        },
        proposalSituationResponse.data
      );
      const cleanCPF = getCleanCPF(proposalData.strCliente || "");
      let proposalDataWithFallback = proposalData;

      if (cleanCPF) {
        try {
          const { data: clientInfo } =
            await clientResource.getClientInfo(cleanCPF);
          proposalDataWithFallback = mergeProposalDataWithClientInfoFallback(
            proposalData,
            clientInfo
          );
        } catch (clientInfoErr) {
          console.error(
            "Error loading client data for proposal edit:",
            clientInfoErr
          );
        }
      }

      preProposalStore.setProposalData(
        {
          ...proposalDataWithFallback,
          queueEditMode
        },
        { skipAlterarSync: true }
      );
      push(ROUTES.preProposalsInfoSecurity);
    } catch (err) {
      console.error("Error starting queue proposal edit:", err);
      toast.error(
        "Erro ao carregar proposta",
        "Não foi possível iniciar a edição. Tente novamente."
      );
    }
  };

  const checkAllDocumentsUploaded = () => {
    return preProposalStore.areAllDocumentsUploaded();
  };

  return {
    ...preProposalStore,
    startPreProposal,
    startQueueProposalEdit,
    getPreProposalInfo,
    syncPreProposal,
    clearPreProposal,
    checkAllDocumentsUploaded
  };
};
